import api from '../../api/client';
import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Row, Col, Form, Select, DatePicker, Space, Card, Spin, message, Typography, Divider, Collapse, List } from 'antd';
import { ReloadOutlined, ClearOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import config from '../../config';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const IncomeStatementList = () => {
    const today = dayjs();
    const defaultStartDate = today.subtract(3, 'month').format('YYYY-MM-DD');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [filters, setFilters] = useState({
        startDate: defaultStartDate,
        endDate: today.format('YYYY-MM-DD'),
        farmId: '',
        farmName: ''
    });
    const [reportBasis, setReportBasis] = useState('accrual');
    const [reportingPeriod, setReportingPeriod] = useState('');
    const [farms, setFarms] = useState([]);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    // Fetch farms
    const fetchFarms = useCallback(async () => {
        try {
            const { data } = await api.get(`/farm`);
            setFarms(data);
        } catch (error) {
            message.error('Failed to fetch farms');
        }
    }, [apiUrl]);

    // Fetch income statement data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...filters,
                basis: reportBasis,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined
            };
            const response = await api.get(`/api/reports/income-statement`, { params });
            if (response.status !== 200) throw new Error('Failed to fetch data');
            setReportingPeriod(response.data.reportingPeriod);
            setData(response.data.data);
        } catch (error) {
            message.error('Failed to fetch income statement');
        } finally {
            setLoading(false);
        }
    }, [filters, reportBasis, apiUrl]);

    useEffect(() => {
        fetchFarms();
        fetchData();
    }, [fetchFarms, fetchData]);

    const handleClearFilters = () => {
        setFilters({
            startDate: defaultStartDate,
            endDate: today.format('YYYY-MM-DD'),
            farmId: '',
            farmName: ''
        });
        setReportBasis('accrual');
    };

    // Helper to normalize category strings
    const normalize = (str) => str?.toUpperCase()?.replace(/\s+/g, '_');

    // Compute P&L totals
    const revenueItems = data.filter((item) => normalize(item.category) === 'REVENUE' && !item.account_name?.includes('Total'));
    const cogsItems = data.filter((item) => normalize(item.category) === 'COST_OF_SALES' && !item.account_name?.includes('Total'));
    const expenseItems = data.filter((item) => normalize(item.category) === 'EXPENSE' && !item.account_name?.includes('Total'));

    const revenueTotal = data.find((item) => normalize(item.category) === 'REVENUE' && item.account_name?.includes('Total'))?.amount || 0;
    const cogsTotal =
        data.find((item) => normalize(item.category) === 'COST_OF_SALES' && item.account_name?.includes('Total'))?.amount || 0;
    const operatingExpensesTotal =
        data.find((item) => normalize(item.category) === 'EXPENSE' && item.account_name?.includes('Total'))?.amount || 0;

    const grossProfit = revenueTotal - cogsTotal;
    const operatingProfit = grossProfit - operatingExpensesTotal;
    const netProfit = operatingProfit;

    // Prepare rows for the table (main categories + calculated rows)
    const pnlRows = [
        {
            key: 'revenue',
            category: 'Revenue',
            total: revenueTotal,
            items: revenueItems,
            isCalculation: false,
            isTotal: false
        },
        {
            key: 'cogs',
            category: 'Cost of Sales',
            total: cogsTotal,
            items: cogsItems,
            isCalculation: false,
            isTotal: false
        },
        {
            key: 'expenses',
            category: 'Operating Expenses',
            total: operatingExpensesTotal,
            items: expenseItems,
            isCalculation: false,
            isTotal: false
        },
        {
            key: 'grossProfit',
            category: 'Gross Profit',
            total: grossProfit,
            items: [],
            isCalculation: true,
            isTotal: false
        },
        {
            key: 'operatingProfit',
            category: 'Operating Profit',
            total: operatingProfit,
            items: [],
            isCalculation: true,
            isTotal: false
        },
        {
            key: 'netProfit',
            category: 'Net Profit/Loss',
            total: netProfit,
            items: [],
            isCalculation: false,
            isTotal: true
        }
    ];

    // Expandable row content: shows the list of accounts under the category (for Revenue, Cost of Sales, Expenses)
    const expandedRowRender = (record) => {
        if (!record.items || record.items.length === 0) {
            return <div style={{ padding: '12px 24px', color: '#999' }}>No detailed accounts</div>;
        }
        return (
            <div style={{ padding: '8px 24px', backgroundColor: '#fafafa' }}>
                <List
                    itemLayout="horizontal"
                    dataSource={record.items}
                    renderItem={(item) => (
                        <List.Item style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                            <List.Item.Meta title={<Text style={{ fontWeight: 500, textTransform: 'capitalize' }}>{item.account_name}</Text>} />
                            <div>{item.amount?.toLocaleString('en-KE', { style: 'currency', currency: 'KES' })}</div>
                        </List.Item>
                    )}
                />
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <span>Total {record.category}</span>
                    <span>{record.total?.toLocaleString('en-KE', { style: 'currency', currency: 'KES' })}</span>
                </div>
            </div>
        );
    };

    // Table columns
    const columns = [
        {
            title: 'Category / Account',
            dataIndex: 'category',
            key: 'category',
            render: (text, record) => (
                <div
                    style={{
                        fontWeight: record.isCalculation || record.isTotal ? 600 : 500,
                        fontStyle: record.isCalculation ? 'italic' : 'normal'
                    }}
                >
                    {text}
                </div>
            )
        },
        {
            title: 'Amount (KES)',
            dataIndex: 'total',
            key: 'amount',
            align: 'right',
            render: (value, record) => (
                <div
                    style={{
                        color: value < 0 ? '#dc3545' : 'inherit',
                        fontWeight: record.isCalculation || record.isTotal ? 600 : 400
                    }}
                >
                    {value?.toLocaleString('en-KE', { style: 'currency', currency: 'KES' })}
                </div>
            )
        }
    ];

    // Export handlers (CSV and PDF)
    const handleDownload = async (type) => {
        setExporting(true);
        try {
            const params = {
                ...filters,
                basis: reportBasis,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined
            };
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/reports/income-statement/export/${type}?${queryString}`;
            window.open(url, '_blank');
            message.success(`Exporting ${type.toUpperCase()}`);
        } catch (error) {
            message.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <Card title="Filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Farm">
                            <Select
                                allowClear
                                placeholder="Select farm"
                                options={farms.map((f) => ({ label: f.name, value: f.id }))}
                                value={filters.farmId || null}
                                onChange={(val, opt) => setFilters((prev) => ({ ...prev, farmId: val, farmName: opt?.label }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Date Range">
                            <RangePicker
                                value={[
                                    filters.startDate ? dayjs(filters.startDate) : null,
                                    filters.endDate ? dayjs(filters.endDate) : null
                                ]}
                                onChange={(dates) => {
                                    if (!dates) return;
                                    setFilters((prev) => ({
                                        ...prev,
                                        startDate: dates[0].format('YYYY-MM-DD'),
                                        endDate: dates[1].format('YYYY-MM-DD')
                                    }));
                                }}
                                presets={[
                                    { label: 'Today', value: [dayjs(), dayjs()] },
                                    { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                                    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                    {
                                        label: 'Last Month',
                                        value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
                                    },
                                    { label: 'Last 3 Months', value: [dayjs().subtract(3, 'month'), dayjs()] },
                                    { label: 'Last 6 Months', value: [dayjs().subtract(6, 'month'), dayjs()] },
                                    { label: 'This Year', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                                    {
                                        label: 'Last Year',
                                        value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
                                    }
                                ]}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Report Basis">
                            <Select
                                options={[
                                    { label: 'Cash Basis', value: 'cash' },
                                    { label: 'Accrual Basis', value: 'accrual' }
                                ]}
                                value={reportBasis}
                                onChange={setReportBasis}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                                Fetch
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                                Clear
                            </Button>
                            <Button icon={<FilePdfOutlined />} onClick={() => handleDownload('pdf')} loading={exporting}>
                                PDF
                            </Button>
                            <Button icon={<DownloadOutlined />} onClick={() => handleDownload('csv')} loading={exporting}>
                                CSV
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginTop: 24 }} title="INCOME STATEMENT">
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Title level={5}>{filters.farmName || 'SFARMS'}</Title>
                    <Title level={5}>Income Statement</Title>
                    <Text type="secondary">
                        From: {dayjs(filters.startDate).format('DD/MM/YYYY')} To: {dayjs(filters.endDate).format('DD/MM/YYYY')}
                    </Text>
                    <br />
                    <Text type="secondary">As of {reportingPeriod}</Text>
                    <br />
                    <Text type="secondary">Basis: {reportBasis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}</Text>
                </div>

                <Spin spinning={loading}>
                    <Table
                        size="small"
                        columns={columns}
                        dataSource={pnlRows}
                        rowKey="key"
                        expandable={{
                            expandedRowRender,
                            rowExpandable: (record) => record.items && record.items.length > 0
                        }}
                        pagination={false}
                        bordered
                        // pagination={{
                        //         showSizeChanger: true,
                        //         showQuickJumper: true
                        //     }}
                        scroll={{ x: 1200 }}
                    />
                </Spin>
            </Card>
        </>
    );
};

export default IncomeStatementList;
